import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const ITEMS = [
  { key: "claims_assisted", labelKey: "traction.claims", suffix: "+", fallback: 100000, tone: "from-blue-800 to-blue-950" },
  { key: "settlement_crore", labelKey: "traction.settlements", prefix: "₹", suffix: " Cr", fallback: 142, tone: "from-orange-500 to-orange-600" },
  { key: "network_workshops", labelKey: "traction.garages", suffix: "+", fallback: 12000, tone: "from-emerald-500 to-emerald-700" },
  { key: "insurance_partners", labelKey: "traction.partners", suffix: "+", fallback: 25, tone: "from-pink-500 to-pink-700" },
  { key: "cities_covered", labelKey: "traction.cities", suffix: "+", fallback: 1000, tone: "from-amber-500 to-amber-700" },
];

function useCountUp(target, duration = 1200) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!target || typeof target !== "number") { setN(target); return; }
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

function StatCard({ item, value }) {
  const { t } = useI18n();
  const isNumber = typeof value === "number";
  const shown = useCountUp(isNumber ? value : 0);
  const displayValue = isNumber ? shown.toLocaleString("en-IN") : (value ?? "24×7");
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="card-premium p-6 relative overflow-hidden group"
      data-testid={`stat-${item.key}`}
    >
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity bg-gradient-to-br ${item.tone}`} />
      <div className="relative">
        <div className={`font-heading font-black text-3xl md:text-4xl tracking-tight bg-gradient-to-br ${item.tone} bg-clip-text text-transparent`}>
          {item.prefix ?? ""}{displayValue}
          {item.suffix && <span>{item.suffix}</span>}
        </div>
        <div className="text-xs mt-2 font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
          {t(item.labelKey)}
        </div>
      </div>
    </motion.div>
  );
}

export default function TrustCounters() {
  const { t } = useI18n();
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <section className="py-16 relative mesh-subtle" data-testid="trust-counters">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 dark:text-blue-400 mb-2">{t("traction.eyebrow")}</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            {t("traction.title")}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {ITEMS.map((it) => {
            const v = stats[it.key] ?? it.fallback;
            return <StatCard key={it.key} item={it} value={v} />;
          })}
        </div>
      </div>
    </section>
  );
}
